import {
  IProjectSettings,
  ITraceGraphData,
  ITraceRule,
  ITraceRuleArray,
} from './Interfaces';
import { Plugin } from './Plugin';
import { sdkInstance } from './Instance';
import {
  IApp,
  IDashboardPage,
  IDashboardParametersBase,
  IDataStorage,
  ISearchResult,
  Project,
  ReferenceTools,
  TrimNeedle,
} from 'matrix-requirements-sdk/client';

const ml = sdkInstance.ml;
const app = sdkInstance.app as IApp;
const matrixsdk = sdkInstance.matrixsdk;
const globalMatrix = sdkInstance.globalMatrix;
const UIToolsConstants = sdkInstance.UIToolsConstants;
const refLinkStyle = sdkInstance['refLinkStyle'];
const refLinkTooltip = sdkInstance['refLinkTooltip'];

export interface IDashboardParameters extends IDashboardParametersBase {
  cat: string;
}

// eslint-disable-next-line no-unused-vars
export class DashboardPage implements IDashboardPage<IDashboardParameters> {
  settings: IProjectSettings;

  _root: JQuery;

  constructor(
    private project: Project,
    private projectStorage: IDataStorage,
    private popupModeOrControl = false,
    private currentFolder: string = undefined,
  ) {
    this.settings = {
      ...Plugin.config.projectSettingsPage.defaultSettings,
      ...globalMatrix.ItemConfig.getSettingJSON(
        Plugin.config.projectSettingsPage.settingName,
        {},
      ),
    };
  }

  /** Customize static HTML here */
  private getDashboardDOM(): JQuery {
    return $(this.dashboardDOM);
  }

  currentCat = '';

  public AllCoveredColor: string = UIToolsConstants.CIColors.GreenSushi.color;
  public MissingUp: string = UIToolsConstants.CIColors.YellowBrightSun.color;
  public MissingDown: string = UIToolsConstants.CIColors.OrangeYellow.color;
  public MissingAll: string = UIToolsConstants.CIColors.RedPersimmon.color;

  public async SelectionChanged(cat: string, folder?: string) {
    if (cat == undefined) {
      return;
    }

    this.currentCat = cat;
    this.currentFolder = folder;
    if (cat != '') $('#selectedCat', app.itemForm).text(cat);
    else $('#selectedCat', app.itemForm).text('All');

    //No save in case of popup
    if (!this.popupModeOrControl) this.setLastSelection(cat);

    $('#waitForIt', this._root).html('');
    $('#waitForIt', this._root).append(ml.UI.getSpinningWait('please wait...'));

    $('.spinningWait', this._root).show();

    $('.addedNotifRow', this._root).remove();

    let results = await this.getItems(cat, folder);
    let data = this.calculateTraceData(results, this.rules);
    this.renderTraceTable(results, data, this._root, this.rules);
    $('#TRACEContentPanel', this._root).show();
    $('#waitForIt', this._root).html('');

    setTimeout((o: any) => {
      this.drawChart(data);
    }, 100);
  }
  async getItems(cat: string, folder?: string) {
    const query = [];
    if (cat != '') {
      query.push(' category="' + cat + '"');
    } else {
      const categories = globalMatrix.ItemConfig.getTraceConfig().rules.map(
        (o) => {
          return o.category;
        },
      );
      query.push(
        '(' +
          categories
            .filter((o: any) => {
              return (
                this.rules[o].downMust.length != 0 ||
                this.rules[o].upMust.length != 0
              );
            })
            .map((o) => {
              return 'CATEGORY = "' + o + '"';
            })
            .join(' OR ') +
          ')',
      );
    }
    if (folder && folder != '') {
      query.push('folderm=' + folder);
    }
    const fullQuery = 'mrql:' + query.join(' AND ');
    const labelFilter = ml.LabelTools.getFilter();
    const labelFilterArg =
      (labelFilter && labelFilter.length) > 0 ? labelFilter : undefined;
    const results: ISearchResult[] = await matrixsdk.search(
      fullQuery,
      false,
      true,
      false,
      labelFilterArg,
    );

    return results;
  }
  start() {
    // No automatic start in case of popup
    if (this.popupModeOrControl) return;
    // Get last opened Notif for that user.

    const cat = this.getLastSelection();
    this.SelectionChanged(cat);
  }
  getLastSelection(): string {
    const value = globalMatrix.projectStorage.getItem('lastTRACESelection');
    if (value != undefined && value != '') return value;
    return '';
  }
  setLastSelection(sel: string) {
    globalMatrix.projectStorage.setItem('lastTRACESelection', sel);
  }

  private currentFilter = '';
  filterByType(filter: any) {
    this.currentFilter = filter.type;
    if (filter.type == '') {
      //Show all
      $('#itemTraceList tbody tr', this._root).show();
    } else {
      $('#itemTraceList tbody tr', this._root).hide();
      $(
        "#itemTraceList tbody tr[data-type='" + filter.type + "']",
        this._root,
      ).show();
    }
  }
  calculateTraceData(
    needles: ISearchResult[],
    rules: ITraceRuleArray,
  ): ITraceGraphData {
    const data: ITraceGraphData = {
      allItems: [],
      itemsWithAllCoverageMissing: [],
      itemsWithDownTraceMissing: [],
      itemWithUpTraceMissing: [],
      hasDownMust: false,
      hasUpMust: false,
    };
    if (needles) {
      needles.forEach((needle) => {
        const rule = rules[ml.Item.parseRef(needle.itemId).type];
        let missingDown = false;
        let missingUp = false;

        // Checking down trace rules
        rule.downMust.forEach((downRule) => {
          let count = 0;
          if (needle.downlinks) {
            needle.downlinks.forEach((downItem) => {
              const cat = downItem.substring(0, downItem.indexOf('-'));
              if (downRule.indexOf(cat) !== -1) {
                count++;
              }
            });
          }
          if (count === 0) {
            missingDown = true;
            data.hasDownMust = true;
          }
        });

        // Checking up trace rules
        rule.upMust.forEach((upRule) => {
          let count = 0;
          if (needle.uplinks) {
            needle.uplinks.forEach((upItem) => {
              const cat = upItem.substring(0, upItem.indexOf('-'));
              if (upRule.indexOf(cat) !== -1) {
                count++;
              }
            });
          }
          if (count === 0) {
            missingUp = true;
            data.hasUpMust = true;
          }
        });

        // Update data based on missing traces
        if (missingDown && missingUp) {
          data.itemsWithAllCoverageMissing.push(needle);
        } else if (missingDown && !missingUp) {
          data.itemsWithDownTraceMissing.push(needle);
        } else if (!missingDown && missingUp) {
          data.itemWithUpTraceMissing.push(needle);
        }
        data.allItems.push(needle);
      });
    }
    return data;
  }

  renderTraceItems(
    data: ITraceGraphData,
    rootElement: JQuery,
    rules: ITraceRuleArray,
  ): void {
    const table = $('#itemTraceList', rootElement);
    const template = $('.template', table);
    $('.addedItem', table).remove();
    $('.uplinks', rootElement).toggle(Object.keys(data.hasUpMust).length > 0);
    $('.downlinks', rootElement).toggle(
      Object.keys(data.hasDownMust).length > 0,
    );

    data.allItems.forEach((item) => {
      let itemCat = ml.Item.parseRef(item.itemId).type;
      let jqueryItem = template
        .clone()
        .removeClass('template hidden')
        .addClass('addedItem');
      jqueryItem.attr('data-id', item.itemId);
      $('.title', jqueryItem).html('');

      const itemId = item.itemId;
      const itemLink = $('.linkTrace', jqueryItem);
      const link = $('<div></div>').refLink({
        id: itemId,
        title: item.title,
        style: refLinkStyle.link,
        tooltip: refLinkTooltip.html,
        hideTitle: false,
      });

      if (data.itemsWithAllCoverageMissing.includes(item)) {
        jqueryItem.find('.linkTrace').css('color', this.MissingAll);
        jqueryItem.attr('data-type', 'missingAll');
      } else if (data.itemWithUpTraceMissing.includes(item)) {
        jqueryItem.find('.linkTrace').css('color', this.MissingUp);
        jqueryItem.attr('data-type', 'missingUp');
      } else if (data.itemsWithDownTraceMissing.includes(item)) {
        jqueryItem.find('.linkTrace').css('color', this.MissingDown);
        jqueryItem.attr('data-type', 'missingDown');
      } else {
        jqueryItem.find('.linkTrace').css('color', this.AllCoveredColor);
        jqueryItem.attr('data-type', 'missingNone');
      }
      itemLink.click(() => {
        const rt = new sdkInstance.ReferenceTools();
        app.getItemAsync(itemId).then((asyncItem) => {
          //TODO :find out if i can edit or not.
          rt.showReferenceDialog({
            item: asyncItem,
            canEdit: true,
            callback: (item) => {
              //Refresh all
              this.SelectionChanged(this.currentCat, this.currentFolder);
            },
          });
        });
      });
      $('.title', jqueryItem).append(link);
      $('.title', jqueryItem).data('ref', itemId);
      $('.uplinks', jqueryItem).text(item.uplinks ? item.uplinks.length : 0);
      $('.downlinks', jqueryItem).text(
        item.downlinks ? item.downlinks.length : 0,
      );
      let details: string[] = [];
      if (rules[itemCat]?.downMust.length > 0) {
        // Check downmust rules to find out what's missing.
        const downlinks = item.downlinks || [];
        for (const downRule of rules[itemCat].downMust) {
          const hasMatchingDownlink = downlinks.some((downItem) => {
            const cat = downItem.split('-')[0];
            return downRule.includes(cat);
          });

          if (!hasMatchingDownlink) {
            details.push(`Missing downlink to ${downRule.join(', ')}`);
          }
        }
      }
      if (rules[itemCat]?.upMust.length > 0) {
        // Check downmust rules to find out what's missing.
        const uplinks = item.uplinks || [];
        for (const upRule of rules[itemCat].upMust) {
          const hasMatchingUplink = uplinks.some((downItem) => {
            const cat = downItem.split('-')[0];
            return upRule.includes(cat);
          });

          if (!hasMatchingUplink) {
            details.push(`Missing uplink to ${upRule.join(', ')}`);
          }
        }
      }
      if (details.length == 0) {
      }
      $('.details', jqueryItem).text(details.join(', '));

      $('tbody', table).append(jqueryItem);
    });

    $('#itemTraceList tbody tr').show();
  }

  renderTraceTable(
    needles: ISearchResult[],
    data: ITraceGraphData,
    rootElement: JQuery,
    rules: ITraceRuleArray,
  ): ITraceGraphData {
    this.renderTraceItems(data, rootElement, rules);
    return data;
  }

  private hasTraceability(
    categories: string[],
    rules: ITraceRuleArray,
  ): boolean {
    const traceRules = categories.filter((o: any) => {
      return rules[o].downMust.length != 0 || rules[o].upMust.length != 0;
    });
    return traceRules.length > 0;
  }
  init() {
    this.rules = DashboardPage.getRules();
  }
  render(): JQuery {
    this.init();
    const categories = globalMatrix.ItemConfig.getTraceConfig().rules.map(
      (o) => {
        return o.category;
      },
    );

    this._root = this.getDashboardDOM();

    // If a project has no traceability rules, we'll only render a simple message.
    if (!this.hasTraceability(categories, this.rules)) {
      this._root.html(
        '<p style="padding: 5px;">No traceability rules are configured.</p>',
      );
      return this._root;
    }

    const baseControl = $("<div id='itemSelectionTRACE'/>");

    $('.toolbarButtons', app.itemForm).append(baseControl);

    const select = $(`<div class="dropdown navbar-right" style="">
                <button class="btn btn-xs dropdown-toggle" type="button" data-toggle="dropdown">
                    <span id="selectedCat" >CAT</span>&nbsp;
                    <span class="caret"></span>
                </button>
                <ul class="dropdown-menu">
                </ul>
                </div>`);

    baseControl.append(select);

    if (this.popupModeOrControl) {
      baseControl.hide();
    }
    //Table filter
    $('#TRACEInputFilter', this._root).on('keyup', (e) => {
      const value = $(e.target).val().toLowerCase();
      $('#itemTraceList tbody tr').show();

      $('#itemTraceList tbody tr').each((index, elem) => {
        if ($(elem).text().toLowerCase().indexOf(value) == -1) {
          $(elem).hide();
        }
      });
    });

    if (!this.popupModeOrControl) {
      setTimeout((o: any) => this.start(), 10);
    }
    categories
      .filter((cat) => {
        return (
          this.rules[cat].upMust.length != 0 ||
          this.rules[cat].downMust.length != 0
        );
      })
      .forEach((cat) => {
        const item = $(
          `<li class="cat" data-cat="${cat}"><a href="javascript:void(0)">${cat}</a></li>`,
        ).click(() => {
          this.SelectionChanged(cat);
        });
        $('.dropdown-menu', select).append(item);
      });

    const allItem = $(
      `<li class="cat" data-cat=""><a href="javascript:void(0)">All</a></li>`,
    ).click(() => {
      this.SelectionChanged('');
    });
    $('.dropdown-menu', select).prepend(allItem);

    this.installCopyButtons('Traceability overview');
    return this._root;
  }

  /** Add interactive element in this function */
  renderProjectPage() {
    app.itemForm.append(
      ml.UI.getPageTitle(
        Plugin.config.dashboard.title,
        () => {
          return this._root;
        },
        () => {
          this.onResize();
        },
      ),
    );
    app.itemForm.append(this.render());
  }

  onResize() {
    /* Will be triggered when resizing. */
  }

  static getRules(): ITraceRuleArray {
    const rules: ITraceRuleArray = {};
    globalMatrix.ItemConfig.getTraceConfig().rules.forEach((o: any) => {
      rules[o.category] = {
        cat: o.category,
        upMust: o.up_rules
          .filter((up: any) => {
            return up.rule == 'must_have';
          })
          .map((up: any) => {
            return up.any_of;
          }),
        upCan: o.up_rules
          .filter((up: any) => {
            return up.rule == 'can_have';
          })
          .map((up: any) => {
            return up.any_of;
          }),
        downMust: o.down_rules
          .filter((dw: any) => {
            return dw.rule == 'must_have';
          })
          .map((down: any) => {
            return down.any_of;
          }),
        downCan: o.down_rules
          .filter((dw: any) => {
            return dw.rule == 'can_have';
          })
          .map((down: any) => {
            return down.any_of;
          }),
      };
    });
    return rules;
  }

  rules: ITraceRuleArray;
  chart: c3.ChartAPI;

  installCopyButtons(title: string) {
    let savedWidth = 0;
    ml.UI.copyBuffer(
      $('#tracePieChartTitle', this._root),
      'copy  to clipboard',
      $(this._root),
      this._root,
      (copied: JQuery) => {
        $('#traceTableHeader i', copied).remove();
        $('#itemTraceList th:first-of-type', copied).remove();
        $('#itemTraceList tr.template', copied).remove();
        $('.title', copied).each((i, item) => {
          $(item).text($(item).data('ref') + '!');
        });
        $('.addedItem td:first-of-type', copied).remove();

        ml.UI.fixC3ForCopy(copied);
        $('#TRACEInputFilter', copied).remove();
        $('#tracePieChartTitle', copied).html(
          '<h1>' +
            title +
            '</h1> <span> <b> Date:</b> ' +
            ml.UI.DateTime.renderCustomerHumanDate(new Date()) +
            '<br/>',
        );
      },
      '',
      () => {
        savedWidth = $('#TRACEContentPanel svg').width();
        this.chart.resize({ width: 590 });
      },
      () => {
        this.chart.resize({ width: savedWidth });
      },
    );
  }
  private drawChart(inputData: ITraceGraphData) {
    const colors: string[] = [];
    const colums: any[] = [];
    colums.push([
      'All covered',
      inputData.allItems.length -
        inputData.itemWithUpTraceMissing.length -
        inputData.itemsWithAllCoverageMissing.length -
        inputData.itemsWithDownTraceMissing.length,
      UIToolsConstants.CIColors.GreenSushi.color,
      'missingNone',
    ]);
    if (inputData.hasUpMust) {
      colums.push([
        'Missing up link',
        inputData.itemWithUpTraceMissing.length,
        UIToolsConstants.CIColors.YellowBrightSun.color,
        'missingUp',
      ]);
    }
    if (inputData.hasDownMust) {
      colums.push([
        'Missing down link',
        inputData.itemsWithDownTraceMissing.length,
        UIToolsConstants.CIColors.OrangeYellow.color,
        'missingDown',
      ]);
    }
    colums.push([
      'Missing up and down links',
      inputData.itemsWithAllCoverageMissing.length,
      UIToolsConstants.CIColors.RedPersimmon.color,
      'missingAll',
    ]);

    colums.forEach((col) => {
      col[0] += ' (' + col[1] + ')';
    });

    this.chart = c3.generate({
      bindto: $('#tracePieChart', this._root)[0],
      data: {
        columns: colums,
        type: 'donut',
        onclick: (d, i) => {
          this.filterByType({
            type: colums[d.index][colums[d.index].length - 1],
          });
        },
        color: (color, d: string) => {
          const c = colums.filter((item) => {
            return item[0] == d;
          });
          if (c.length > 0) {
            return c[0][c[0].length - 2];
          }
          return color;
        },
      },
      donut: {
        label: {
          format: (value, ratio, id) => {
            return value;
          },
        },
      },
      legend: {
        item: {
          onclick: (id) => {
            const c = colums.filter((item) => {
              return item[0] == id;
            });
            if (c.length > 0) {
              this.filterByType({ type: c[0][c[0].length - 1] });
            }
          },
        },
        position: 'inset',
        inset: {
          anchor: 'top-right',
        },
      },
      tooltip: {
        format: {
          value: (value: any, ratio: any, id: any, index: any) => {
            return value;
          },
        },
      },
    });

    $('#tracePieChart .c3-event-rects').click(() => {
      this.filterByType({ type: '' });
    });

    return;
  }

  private dashboardDOM = `<div class="panel-body-v-scroll fillHeight">
        <style>
        .chart {
            width: 90%;
            min-height: 350px;
            cursor:pointer;
        }
        .bigChart
        {
            min-height:700px;
        }
        </style>
        <div class="row" id="waitForIt" class="spinningWait"></div>
        <div class="panel-body" id="TRACEContentPanel">

            <div id="">
                <div class="panel panel-default">
                <div class="panel-heading">
                    <h3 class="panel-title" id="tracePieChartTitle">Overview</h3>
                </div>
                <div class="panel-body">
                    <div class="traceTitleForCopy"></div>
                    <div id="tracePieChart" class="chart"></div>
                </div>
        </div></div>
        <div id="traceContent" >
        <div class="row doNotCopy">
            <div class="col-lg-3 ">
                <h3 id="TraceTableHeader">Item list</h3>
            </div>
            <div class=" col-lg-7">
            </div>
            <div class=" col-lg-2">
            <input type="text" id="TRACEInputFilter" style="margin-bottom:10px;" placeholder="filter..." class="doNotCopy  form-control"></input>
            </div>
        </div>
        <div class="traceTitleForCopy"></div>
            <table id="itemTraceList" class="table table-condensed table-borderless table-hover">
               <thead>
                <tr>
                    <th style="width:20px"> </th>
                    <th> id</th>
                    <th class="uplinks" style="width:40px"> Uplinks</th>
                    <th class="downlinks" style="width:40px"> Downlinks</th>
                    <th> Details</th>
                </tr>
               </thead>
               <tbody>
                    <tr class="template hidden">
                        <td style="display:flex"><i class="fal fa-sitemap linkTrace"/></td>
                        <td class="title" >MyITEM : my title ablash </td>
                        <td class="uplinks"> xx</td>
                        <td class="downlinks"> xx</td>
                        <td class=""> <small class="details">Missing TC and SPEC</small></td>
                    </tr>
                </tbody>
            </table>
            </div>
        </div>
        </div>
        `;
}
